import React, { useRef, useEffect } from 'react'
import { OverlayTrigger, Popover, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import VendorSearch from 'dashboard/filters/vendors'

function VendorNameFilterableHeader({
  column,
  title,
  params,
  setParams,
  includeOrdering = true,
  sortColumn,
  dashboardSetParams = null,
  popoverId = '',
  activePopoverId = null,
  setActivePopoverId = null,
  canEdit = true,
  initialSearch = ''
}) {
  const showPopover = activePopoverId === popoverId
  const target = useRef(null)
  const childHandleApplyRef = useRef(null)

  // Scroll the column header into view when the popover opens
  useEffect(() => {
    if (showPopover && target.current) {
      target.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }, [showPopover])

  const handleTogglePopover = (show, isFromHandleApply = false) => {
    if (setActivePopoverId) {
      // If closing the popover and we have a child handleApply function, call it
      // but only if it's not already being called from handleApply
      if (!show && !isFromHandleApply && childHandleApplyRef.current) {
        childHandleApplyRef.current()
      }
      setActivePopoverId(show ? popoverId : null)
    }
  }

  // Enhanced setParams that updates both table state and dashboard state
  const enhancedSetParams = (newParams) => {
    // Always update the table state for immediate UI response
    setParams(newParams)

    // Also update dashboard state if available for persistence
    if (dashboardSetParams) {
      dashboardSetParams(newParams)
    }
  }

  const renderFilterPopover = () => {
    const popover = (
      <Popover id={`popover-${column.accessor_key}`} className="filterable-header-popover">
        <Popover.Body>
          <div>
            {includeOrdering && sortColumn && (
              <>
                <div>
                  <div className="mb-2">
                    <strong className="small">Sort</strong>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant={
                        params.orderBy === sortColumn &&
                        params.orderDirection === 'asc'
                          ? 'primary'
                          : 'light'
                      }
                      size="sm"
                      onClick={() => {
                        if (params.orderBy === sortColumn && params.orderDirection === 'asc') {
                          enhancedSetParams({
                            ...params,
                            orderBy: null,
                            orderDirection: null,
                            page: 1
                          })
                        } else {
                          enhancedSetParams({
                            ...params,
                            orderBy: sortColumn,
                            orderDirection: 'asc',
                            page: 1
                          })
                        }
                      }}
                    >
                      Sort Asc
                    </Button>
                    <Button
                      variant={
                        params.orderBy === sortColumn &&
                        params.orderDirection === 'desc'
                          ? 'primary'
                          : 'light'
                      }
                      size="sm"
                      onClick={() => {
                        if (params.orderBy === sortColumn && params.orderDirection === 'desc') {
                          enhancedSetParams({
                            ...params,
                            orderBy: null,
                            orderDirection: null,
                            page: 1
                          })
                        } else {
                          enhancedSetParams({
                            ...params,
                            orderBy: sortColumn,
                            orderDirection: 'desc',
                            page: 1
                          })
                        }
                      }}
                    >
                      Sort Desc
                    </Button>
                  </div>
                </div>
              </>
            )}

            <hr className="my-2" />

            <VendorSearch
              params={params}
              setParams={enhancedSetParams}
              onClose={() => handleTogglePopover(false, true)}
              onSetHandleApply={(handleApply) => { childHandleApplyRef.current = handleApply }}
              canEdit={canEdit}
              initialSearch={initialSearch}
            />
          </div>
        </Popover.Body>
      </Popover>
    )

    const isActive = hasActiveFilter() || (params.orderBy === sortColumn)

    return (
      <OverlayTrigger
        trigger="click"
        placement="bottom"
        overlay={popover}
        show={showPopover}
        onToggle={(nextShow) => {
          if (!nextShow) {
            handleTogglePopover(false, false)
          }
        }}
        rootClose
      >
        <Button
          ref={target}
          variant={isActive ? 'primary' : 'light'}
          size="sm"
          className="ms-1 border-0"
          style={{
            padding: '2px 6px',
            minWidth: 'auto'
          }}
          onClick={(e) => {
            e.stopPropagation()
            handleTogglePopover(!showPopover)
          }}
          title={!canEdit ? 'View only - only the creator or permitted users can edit filters' : ''}
        >
          <FontAwesomeIcon icon={faFilter} size="xs" />
        </Button>
      </OverlayTrigger>
    )
  }

  const hasActiveFilter = () => {
    // Check if there are any vendors or excludedVendors
    const vendors = params.vendors || []
    const excludedVendors = params.excludedVendors || []
    return vendors.length > 0 || excludedVendors.length > 0
  }

  return (
    <th style={{ cursor: 'default' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        {title}
        {column && renderFilterPopover()}
      </span>
    </th>
  )
}

export default VendorNameFilterableHeader